import { MatchUnitSize } from '@uefadigital/panenka';
import { PkButton, PkDialog, PkIdentifier, PkMatchUnit } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useState, useMemo, Suspense } from 'react';

import { useTicketingOptions } from '../../store';
import { getStadiumComponent } from '../../utils/stadiums';
import StadiumIcon from '../TicketPrices/Stadiums';

import CategoryItem from './CategoryItem';

import { useWindowSize } from '@hooks/useWindowSize';
import { hydrateWithTranslations } from '@services/TranslatorService';
import ErrorBoundary, { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';
import type { MatchWithTickets } from '@shared-types/ticketing.types';
import { dateToString } from '@utilities/dateTime';

import * as styles from './styles.module.scss';

type CategoriesProps = {
  data: MatchWithTickets[];
  isOpen: boolean;
  onClose: () => void;
};

const Categories = ({ data, isOpen, onClose }: CategoriesProps) => {
  const {
    options: { useTeamCode },
  } = useTicketingOptions();

  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const stadium = data?.[0]?.stadium;
  const stadiumName = hydrateWithTranslations(stadium)?.name;
  const [overCategory, setOverCategory] = useState<string>(null);

  const {
    options: { ticketsPortalUrl },
  } = useTicketingOptions();

  const singleMatch = useMemo(() => (data?.length === 1 ? data.at(0) : null), [data]);
  const StadiumComponent = useMemo(() => getStadiumComponent(stadium.id), [stadium.id]);

  return (
    <PkDialog
      className={styles.categoriesDialog}
      isOpen={isOpen}
      onPkClose={onClose}
      size={isMobile ? 'small' : 'large'}
    >
      <div slot='header'>
        <div className={styles.stadiumName}>
          <PkIdentifier className='pk-identifier pk-py--0'>
            {!isMobile && (
              <span slot='prefix'>
                <StadiumIcon stadium={stadium} isClickable={false} />
              </span>
            )}
            <span slot='primary'>{stadiumName}</span>
          </PkIdentifier>
        </div>
      </div>
      <div slot='body'>
        <div>
          <div className={styles.categoriesContent}>
            {StadiumComponent && (
              <Suspense>
                <ErrorBoundary>
                  <StadiumComponent onCategoryOver={setOverCategory} selectedCategory={overCategory} />
                </ErrorBoundary>
              </Suspense>
            )}
            <div
              className={clsx({
                [styles.listCategories]: true,
                [styles[`item--${overCategory}`]]: overCategory,
              })}
            >
              {singleMatch && (
                <>
                  <PkMatchUnit
                    useTeamCode={useTeamCode}
                    className={styles.matchUnit}
                    matchData={singleMatch}
                    size={MatchUnitSize.M}
                    hideRound
                    hideContext
                    hideCta
                  />
                  <PkIdentifier
                    className={clsx('pk-identifier pk-py--0', styles.matchDescription)}
                    alignment='center'
                    primarySize='16'
                  >
                    <span slot='primary'>
                      <span>{`${t('nav_match')} #${singleMatch.matchNumber?.toString().padStart(2, '0')}`} · </span>
                      <span>{dateToString(singleMatch.kickOffTime.date, 'dddd D MMMM')}</span>
                    </span>
                  </PkIdentifier>
                </>
              )}
              <PkIdentifier className='pk-py--0' primarySize='20' secondarySize='14'>
                <span slot='primary'>{t('tckt_offers')}</span>
                <span slot='secondary'>{t('choose_your_zone')}</span>
              </PkIdentifier>

              <CategoryItem catCode='CATEGORY_1' data={data} setOverCategory={setOverCategory} />
              <CategoryItem catCode='CATEGORY_2' data={data} setOverCategory={setOverCategory} />
              <CategoryItem catCode='CATEGORY_3' data={data} setOverCategory={setOverCategory} />
              <CategoryItem catCode='CATEGORY_4' data={data} setOverCategory={setOverCategory} />
              <CategoryItem catCode='FANS_FIRST' data={data} setOverCategory={setOverCategory} />

              {!!ticketsPortalUrl && (
                <div className={styles.ticketsBtn}>
                  <PkButton appearance='outlined' linkHref={ticketsPortalUrl}>
                    {t('tickets')}
                  </PkButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PkDialog>
  );
};

export default withErrorBoundary(memo(Categories));
